import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useTexture, Html } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import * as THREE from 'three';

// ============================================
// DEFAULT PARAMETERS (V8.7 – CYLINDER WIDTH/HEIGHT)
// ============================================
const DEFAULT_ZOOM = 1.13;
const DEFAULT_PARALLAX_STRENGTH = 0.09;
const DEFAULT_CAMERA_MOVE = 0.30;
const DEFAULT_BACKGROUND_SHIFT = 0.10;
const DEFAULT_GHOST_REDUCTION = 0.65;

// Depth processing defaults
const DEFAULT_DEPTH_SMOOTHING = 0.5;
const DEFAULT_EDGE_THRESHOLD = 0.05;
const DEFAULT_DEPTH_CONTRAST = 1.2;
const DEFAULT_BLACK_POINT = 0.0;
const DEFAULT_WHITE_POINT = 0.9;
const DEFAULT_MIDTONE_SHIFT = -0.4;

// Advanced controls defaults
const DEFAULT_EDGE_ENHANCE = 0.3;
const DEFAULT_CORNER_BOOST = 0.2;
const DEFAULT_CYLINDER_STRENGTH = 0.0;
const DEFAULT_CYLINDER_EDGE_CLAMP = 0.5;
const DEFAULT_CYLINDER_GAMMA = 1.0;
const DEFAULT_CYLINDER_WIDTH = 1.0;      // NEW: horizontal spread
const DEFAULT_CYLINDER_HEIGHT = 1.0;     // NEW: vertical spread
const DEFAULT_CORNER_PLUS_STRENGTH = 0.0;
const DEFAULT_CORNER_PLUS_GAMMA = 1.0;
const DEFAULT_LOCAL_CONTRAST = 0.15;
const DEFAULT_MIDTONE_SPREAD = 0.25;
const DEFAULT_MORPH_RADIUS = 2;
const DEFAULT_RADIAL_FALLOFF = 0.0;
const DEFAULT_NOISE_REDUCTION = 0.2;
const DEFAULT_DETAIL_PRESERVE = 0.4;
const DEFAULT_DEPTH_GAMMA = 1.0;

// Model URLs
const DEPTH_ANYWHERE_MODEL_URL = 'https://huggingface.co/onnx-community/depth-anything-v2-small/resolve/main/model.onnx';
const DEPTH_MODEL_INPUT_SIZE = 518;

// ============================================
// ONNX RUNTIME SETUP (FIXED FOR V1.24.3)
// ============================================
let ortModule = null;

const loadONNXRuntime = async () => {
  if (ortModule) return ortModule;
  try {
    ortModule = await import('onnxruntime-web');
    ortModule.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.24.3/dist/';
    ortModule.env.wasm.numThreads = 1;
    ortModule.env.wasm.simd = true;
    ortModule.env.wasm.proxy = false;
    console.log('✅ ONNX Runtime loaded (v1.24.3)');
    return ortModule;
  } catch (error) {
    console.warn('⚠️ ONNX Runtime failed:', error);
    return null;
  }
};

// ============================================
// ERROR BOUNDARY
// ============================================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('Canvas Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.errorContainer}>
          <h3>3D Renderer Error</h3>
          <p>{this.state.error?.message}</p>
          <button style={styles.button} onClick={() => this.setState({ hasError: false })}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================
// UTILITIES: ADVANCED DEPTH PROCESSING
// ============================================
const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Bilateral filter
const bilateralFilter = (depthData, width, height, spatialSigma, rangeSigma) => {
  const result = new Float32Array(width * height);
  const kernelRadius = Math.ceil(spatialSigma * 2.5);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const centerIdx = y * width + x;
      const centerVal = depthData[centerIdx];
      let sum = 0, weightSum = 0;
      for (let dy = -kernelRadius; dy <= kernelRadius; dy++) {
        for (let dx = -kernelRadius; dx <= kernelRadius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny < 0 || ny >= height || nx < 0 || nx >= width) continue;
          const nIdx = ny * width + nx;
          const nVal = depthData[nIdx];
          const spatialDist = Math.sqrt(dx*dx + dy*dy);
          const rangeDist = Math.abs(centerVal - nVal);
          const spatialWeight = Math.exp(-(spatialDist * spatialDist) / (2 * spatialSigma * spatialSigma));
          const rangeWeight = Math.exp(-(rangeDist * rangeDist) / (2 * rangeSigma * rangeSigma));
          const weight = spatialWeight * rangeWeight;
          sum += nVal * weight;
          weightSum += weight;
        }
      }
      result[centerIdx] = sum / weightSum;
    }
  }
  return result;
};

// Unsharp mask
const unsharpMask = (depthData, width, height, strength, threshold) => {
  const blurred = bilateralFilter(depthData, width, height, 1.5, 0.1);
  const result = new Float32Array(width * height);
  for (let i = 0; i < depthData.length; i++) {
    const diff = depthData[i] - blurred[i];
    if (Math.abs(diff) > threshold) {
      result[i] = Math.max(0, Math.min(1, depthData[i] + diff * strength));
    } else {
      result[i] = depthData[i];
    }
  }
  return result;
};

// Corner boost
const applyCornerBoost = (depthData, width, height, strength) => {
  const result = new Float32Array(width * height);
  const cx = width / 2, cy = height / 2;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const dx = (x - cx) / cx;
      const dy = (y - cy) / cy;
      const dist = Math.sqrt(dx*dx + dy*dy) / Math.sqrt(2);
      const cornerWeight = Math.min(1, dist * 1.5);
      const boost = 1.0 + strength * cornerWeight;
      result[idx] = Math.min(1, depthData[idx] * boost);
    }
  }
  return result;
};

// ========== ENHANCED: Vertical Cylinder Curvature (with width/height control) ==========
const applyCylinderCurvature = (depthData, width, height, strength, edgeClamp, gamma, widthFactor, heightFactor) => {
  const result = new Float32Array(width * height);
  const halfW = width / 2;
  const halfH = height / 2;
  // Effective spread: larger factors make the effect wider/taller
  const effectiveW = halfW * widthFactor;
  const effectiveH = halfH * heightFactor;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      // Normalised distance with adjustable spread
      const nx = Math.min(1, Math.abs(x - halfW) / effectiveW);
      const ny = Math.min(1, Math.abs(y - halfH) / effectiveH);
      // Combine horizontal and vertical with emphasis on horizontal (cylinder)
      const dist = Math.sqrt(nx*nx + ny*ny * 0.3); // slight vertical influence
      const clampedDist = Math.pow(dist, edgeClamp * 2 + 0.1);
      const factor = 1.0 - clampedDist * clampedDist;
      let modulation = 1.0 + strength * (factor - 0.5);
      if (gamma !== 1.0) {
        modulation = Math.pow(modulation, gamma);
      }
      result[idx] = Math.max(0, Math.min(1, depthData[idx] * modulation));
    }
  }
  return result;
};

// ========== ENHANCED: Corner‑Plus Emphasis ==========
const applyCornerPlusEmphasis = (depthData, width, height, strength, gamma) => {
  const result = new Float32Array(width * height);
  const cx = width / 2, cy = height / 2;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const dx = Math.abs(x - cx) / cx;
      const dy = Math.abs(y - cy) / cy;
      const cornerWeight = dx * dy;
      let modulation = 1.0 + strength * cornerWeight;
      if (gamma !== 1.0) {
        modulation = Math.pow(modulation, gamma);
      }
      result[idx] = Math.max(0, Math.min(1, depthData[idx] * modulation));
    }
  }
  return result;
};

// Local contrast
const applyLocalContrast = (depthData, width, height, strength, tileSize = 32) => {
  const result = new Float32Array(width * height);
  const halfTile = tileSize / 2;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      let sum = 0, count = 0;
      const startY = Math.max(0, y - halfTile);
      const endY = Math.min(height, y + halfTile);
      const startX = Math.max(0, x - halfTile);
      const endX = Math.min(width, x + halfTile);
      for (let ny = startY; ny < endY; ny++) {
        for (let nx = startX; nx < endX; nx++) {
          sum += depthData[ny * width + nx];
          count++;
        }
      }
      const localMean = sum / count;
      const diff = depthData[idx] - localMean;
      result[idx] = Math.max(0, Math.min(1, localMean + diff * (1 + strength)));
    }
  }
  return result;
};

// Midtone spread
const applyMidtoneSpread = (depthData, strength) => {
  const result = new Float32Array(depthData.length);
  const center = 0.5;
  for (let i = 0; i < depthData.length; i++) {
    let val = depthData[i];
    const diff = val - center;
    val = center + diff * (1 + strength * (1 - Math.abs(diff) * 2));
    result[i] = Math.max(0, Math.min(1, val));
  }
  return result;
};

// Morphological closing
const morphologicalClosing = (depthData, width, height, radius) => {
  const dilated = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let maxVal = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
            maxVal = Math.max(maxVal, depthData[ny * width + nx]);
          }
        }
      }
      dilated[y * width + x] = maxVal;
    }
  }
  const closed = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let minVal = 1;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
            minVal = Math.min(minVal, dilated[ny * width + nx]);
          }
        }
      }
      closed[y * width + x] = minVal;
    }
  }
  return closed;
};

// Radial falloff
const applyRadialFalloff = (depthData, width, height, strength) => {
  const result = new Float32Array(width * height);
  const cx = width / 2, cy = height / 2;
  const maxDist = Math.sqrt(cx*cx + cy*cy);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      const dx = x - cx, dy = y - cy;
      const dist = Math.sqrt(dx*dx + dy*dy) / maxDist;
      const weight = 1.0 - dist * strength;
      result[idx] = depthData[idx] * Math.max(0.5, weight);
    }
  }
  return result;
};

// Detail preservation
const applyDetailPreserve = (depthData, width, height, strength) => {
  const grad = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx = depthData[idx + 1] - depthData[idx - 1];
      const gy = depthData[idx + width] - depthData[idx - width];
      grad[idx] = Math.sqrt(gx*gx + gy*gy);
    }
  }
  let maxGrad = 0;
  for (let i = 0; i < grad.length; i++) maxGrad = Math.max(maxGrad, grad[i]);
  if (maxGrad > 0) {
    for (let i = 0; i < grad.length; i++) grad[i] /= maxGrad;
  }
  const result = new Float32Array(width * height);
  for (let i = 0; i < depthData.length; i++) {
    const edgeFactor = grad[i];
    result[i] = depthData[i] * (1 + strength * edgeFactor);
    result[i] = Math.max(0, Math.min(1, result[i]));
  }
  return result;
};

// Base level adjustments
const adjustDepthLevels = (depthData, contrast, blackPoint, whitePoint, midtoneShift, gamma) => {
  const result = new Float32Array(depthData.length);
  const bp = Math.max(0, Math.min(1, blackPoint));
  const wp = Math.max(bp + 0.01, Math.min(1, whitePoint));
  const range = wp - bp;
  for (let i = 0; i < depthData.length; i++) {
    let val = (depthData[i] - bp) / range;
    val = Math.max(0, Math.min(1, val));
    val = 0.5 + (val - 0.5) * contrast;
    if (midtoneShift !== 0) {
      const g = 1.0 + midtoneShift * 2.0;
      val = Math.pow(val, g);
    }
    if (gamma !== 1.0) {
      val = Math.pow(val, gamma);
    }
    result[i] = Math.max(0, Math.min(1, val));
  }
  return result;
};

// ============================================
// STAGE 1: AI DEPTH ESTIMATION
// ============================================
let depthSession = null;

const loadDepthModel = async () => {
  if (depthSession) return depthSession;
  const ort = await loadONNXRuntime();
  if (!ort) throw new Error('ONNX Runtime unavailable');
  console.log('🧠 Loading Depth Anything V2...');
  try {
    depthSession = await ort.InferenceSession.create(DEPTH_ANYWHERE_MODEL_URL, {
      executionProviders: ['wasm', 'cpu'],
      graphOptimizationLevel: 'basic',
    });
    console.log('✅ Depth model ready');
    return depthSession;
  } catch (error) {
    console.error('Failed to load depth model:', error);
    throw error;
  }
};

const generateAIDepthMap = async (imageElement, params) => {
  const ort = await loadONNXRuntime();
  const session = await loadDepthModel();

  const canvas = document.createElement('canvas');
  canvas.width = DEPTH_MODEL_INPUT_SIZE;
  canvas.height = DEPTH_MODEL_INPUT_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imageElement, 0, 0, DEPTH_MODEL_INPUT_SIZE, DEPTH_MODEL_INPUT_SIZE);
  const imageData = ctx.getImageData(0, 0, DEPTH_MODEL_INPUT_SIZE, DEPTH_MODEL_INPUT_SIZE);

  const mean = [0.485, 0.456, 0.406];
  const std = [0.229, 0.224, 0.225];
  const tensorData = new Float32Array(3 * DEPTH_MODEL_INPUT_SIZE * DEPTH_MODEL_INPUT_SIZE);
  for (let i = 0; i < imageData.data.length; i += 4) {
    const pixel = i / 4;
    const r = imageData.data[i] / 255;
    const g = imageData.data[i + 1] / 255;
    const b = imageData.data[i + 2] / 255;
    tensorData[pixel] = (r - mean[0]) / std[0];
    tensorData[pixel + DEPTH_MODEL_INPUT_SIZE * DEPTH_MODEL_INPUT_SIZE] = (g - mean[1]) / std[1];
    tensorData[pixel + 2 * DEPTH_MODEL_INPUT_SIZE * DEPTH_MODEL_INPUT_SIZE] = (b - mean[2]) / std[2];
  }

  const inputTensor = new ort.Tensor('float32', tensorData, [1, 3, DEPTH_MODEL_INPUT_SIZE, DEPTH_MODEL_INPUT_SIZE]);
  const feeds = { input: inputTensor };
  const results = await session.run(feeds);
  const output = results.output;

  let depthArray = output.data;
  let minVal = Infinity, maxVal = -Infinity;
  for (let i = 0; i < depthArray.length; i++) {
    minVal = Math.min(minVal, depthArray[i]);
    maxVal = Math.max(maxVal, depthArray[i]);
  }
  const range = maxVal - minVal;
  const normalized = new Float32Array(depthArray.length);
  for (let i = 0; i < depthArray.length; i++) {
    normalized[i] = (depthArray[i] - minVal) / range;
  }

  const origW = imageElement.width;
  const origH = imageElement.height;
  const fullDepth = new Float32Array(origW * origH);
  const scaleX = DEPTH_MODEL_INPUT_SIZE / origW;
  const scaleY = DEPTH_MODEL_INPUT_SIZE / origH;

  for (let y = 0; y < origH; y++) {
    for (let x = 0; x < origW; x++) {
      const srcX = x * scaleX;
      const srcY = y * scaleY;
      const x1 = Math.floor(srcX);
      const y1 = Math.floor(srcY);
      const x2 = Math.min(x1 + 1, DEPTH_MODEL_INPUT_SIZE - 1);
      const y2 = Math.min(y1 + 1, DEPTH_MODEL_INPUT_SIZE - 1);
      const fx = srcX - x1;
      const fy = srcY - y1;
      const d11 = normalized[y1 * DEPTH_MODEL_INPUT_SIZE + x1];
      const d21 = normalized[y1 * DEPTH_MODEL_INPUT_SIZE + x2];
      const d12 = normalized[y2 * DEPTH_MODEL_INPUT_SIZE + x1];
      const d22 = normalized[y2 * DEPTH_MODEL_INPUT_SIZE + x2];
      fullDepth[y * origW + x] = d11 * (1 - fx) * (1 - fy) + d21 * fx * (1 - fy) + d12 * (1 - fx) * fy + d22 * fx * fy;
    }
  }

  return applyDepthProcessing(fullDepth, origW, origH, params);
};

const generateHeuristicDepthMap = async (imageElement, params) => {
  const maxDim = 800;
  let w = imageElement.width;
  let h = imageElement.height;
  if (w > maxDim || h > maxDim) {
    const ratio = Math.min(maxDim / w, maxDim / h);
    w = Math.floor(w * ratio);
    h = Math.floor(h * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(imageElement, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const depthValues = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const maxC = Math.max(r, g, b);
      const minC = Math.min(r, g, b);
      const sat = maxC === 0 ? 0 : (maxC - minC) / maxC;
      const yBias = y / h;
      let depth = lum * 0.4 + (1 - sat) * 0.3 + yBias * 0.3;
      depthValues[y * w + x] = Math.min(1, Math.max(0, depth));
    }
  }

  let processed = applyDepthProcessing(depthValues, w, h, params);

  const origW = imageElement.width;
  const origH = imageElement.height;
  const finalDepth = new Float32Array(origW * origH);
  const scaleX = w / origW;
  const scaleY = h / origH;
  for (let y = 0; y < origH; y++) {
    for (let x = 0; x < origW; x++) {
      const srcX = x * scaleX;
      const srcY = y * scaleY;
      const x1 = Math.floor(srcX);
      const y1 = Math.floor(srcY);
      const x2 = Math.min(x1 + 1, w - 1);
      const y2 = Math.min(y1 + 1, h - 1);
      const fx = srcX - x1;
      const fy = srcY - y1;
      const d11 = processed[y1 * w + x1];
      const d21 = processed[y1 * w + x2];
      const d12 = processed[y2 * w + x1];
      const d22 = processed[y2 * w + x2];
      finalDepth[y * origW + x] = d11 * (1 - fx) * (1 - fy) + d21 * fx * (1 - fy) + d12 * (1 - fx) * fy + d22 * fx * fy;
    }
  }

  const depthCanvas = document.createElement('canvas');
  depthCanvas.width = origW;
  depthCanvas.height = origH;
  const depthCtx = depthCanvas.getContext('2d');
  const depthImgData = depthCtx.createImageData(origW, origH);
  for (let i = 0; i < finalDepth.length; i++) {
    const val = Math.round(finalDepth[i] * 255);
    const idx = i * 4;
    depthImgData.data[idx] = val;
    depthImgData.data[idx + 1] = val;
    depthImgData.data[idx + 2] = val;
    depthImgData.data[idx + 3] = 255;
  }
  depthCtx.putImageData(depthImgData, 0, 0);

  return {
    depthMapUrl: depthCanvas.toDataURL('image/png'),
    depthData: finalDepth,
    width: origW,
    height: origH,
  };
};

const applyDepthProcessing = (depthData, width, height, params) => {
  let processed = depthData;
  
  processed = bilateralFilter(processed, width, height, params.depthSmoothing, params.edgeThreshold);
  
  if (params.noiseReduction > 0) {
    processed = bilateralFilter(processed, width, height, params.noiseReduction * 2, params.edgeThreshold);
  }
  
  if (params.cornerBoost > 0) {
    processed = applyCornerBoost(processed, width, height, params.cornerBoost);
  }

  if (params.cylinderStrength !== 0) {
    processed = applyCylinderCurvature(
      processed, width, height,
      params.cylinderStrength,
      params.cylinderEdgeClamp,
      params.cylinderGamma,
      params.cylinderWidth,
      params.cylinderHeight
    );
  }

  if (params.cornerPlusStrength !== 0) {
    processed = applyCornerPlusEmphasis(
      processed, width, height,
      params.cornerPlusStrength,
      params.cornerPlusGamma
    );
  }
  
  if (params.localContrast > 0) {
    processed = applyLocalContrast(processed, width, height, params.localContrast);
  }
  
  if (params.midtoneSpread > 0) {
    processed = applyMidtoneSpread(processed, params.midtoneSpread);
  }
  
  if (params.radialFalloff > 0) {
    processed = applyRadialFalloff(processed, width, height, params.radialFalloff);
  }
  
  if (params.detailPreserve > 0) {
    processed = applyDetailPreserve(processed, width, height, params.detailPreserve);
  }
  
  processed = adjustDepthLevels(
    processed,
    params.depthContrast,
    params.blackPoint,
    params.whitePoint,
    params.midtoneShift,
    params.depthGamma
  );
  
  if (params.edgeEnhance > 0) {
    processed = unsharpMask(processed, width, height, params.edgeEnhance, params.edgeThreshold);
  }
  
  if (params.morphRadius > 0) {
    processed = morphologicalClosing(processed, width, height, Math.floor(params.morphRadius));
  }
  
  return processed;
};

// ============================================
// BACKGROUND PREPARATION
// ============================================
const createBlurredBackground = async (imageDataUrl, blurAmount = 20) => {
  const img = await loadImageElement(imageDataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.filter = `blur(${blurAmount}px)`;
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
};

// ============================================
// SHADER PARALLAX RENDERER
// ============================================
const ShaderParallax = ({ 
  imageUrl, depthUrl, backgroundUrl, isActive, gyroEnabled,
  zoom, parallaxStrength, cameraMoveAmount, backgroundShift, ghostReduction,
}) => {
  const { camera, size } = useThree();
  const meshRef = useRef();
  const bgMeshRef = useRef();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [gyroPos, setGyroPos] = useState({ x: 0, y: 0 });
  const [progress, setProgress] = useState(0);
  const startTime = useRef(0);

  const imageTex = useTexture(imageUrl);
  const depthTex = useTexture(depthUrl);
  const bgTex = useTexture(backgroundUrl);

  useEffect(() => {
    if (imageTex) {
      imageTex.minFilter = THREE.LinearFilter;
      imageTex.magFilter = THREE.LinearFilter;
      imageTex.wrapS = THREE.ClampToEdgeWrapping;
      imageTex.wrapT = THREE.ClampToEdgeWrapping;
    }
    if (depthTex) {
      depthTex.minFilter = THREE.LinearFilter;
      depthTex.magFilter = THREE.LinearFilter;
      depthTex.wrapS = THREE.ClampToEdgeWrapping;
      depthTex.wrapT = THREE.ClampToEdgeWrapping;
    }
    if (bgTex) {
      bgTex.minFilter = THREE.LinearFilter;
      bgTex.magFilter = THREE.LinearFilter;
    }
  }, [imageTex, depthTex, bgTex]);

  useEffect(() => {
    const handleMouse = (e) => {
      const wrapper = document.getElementById('canvas-wrapper');
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  useEffect(() => {
    if (!gyroEnabled) return;
    const handleOrientation = (e) => {
      if (e.beta === null || e.gamma === null) return;
      const beta = Math.max(-20, Math.min(20, e.beta)) / 20;
      const gamma = Math.max(-20, Math.min(20, e.gamma)) / 20;
      setGyroPos({ x: gamma, y: beta });
    };
    window.addEventListener('deviceorientation', handleOrientation);
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [gyroEnabled]);

  useEffect(() => {
    if (isActive) {
      startTime.current = performance.now();
      setProgress(0);
    }
  }, [isActive]);

  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        u_image: { value: imageTex },
        u_depth: { value: depthTex },
        u_mouse: { value: new THREE.Vector2(0, 0) },
        u_gyro: { value: new THREE.Vector2(0, 0) },
        u_strength: { value: parallaxStrength },
        u_progress: { value: 0 },
        u_aspect: { value: 1 },
        u_imageAspect: { value: 1 },
        u_planeAspect: { value: 1 },
        u_useGyro: { value: gyroEnabled ? 1.0 : 0.0 },
        u_ghostReduction: { value: ghostReduction },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D u_image;
        uniform sampler2D u_depth;
        uniform vec2 u_mouse;
        uniform vec2 u_gyro;
        uniform float u_strength;
        uniform float u_progress;
        uniform float u_aspect;
        uniform float u_imageAspect;
        uniform float u_planeAspect;
        uniform float u_useGyro;
        uniform float u_ghostReduction;
        varying vec2 vUv;

        void main() {
          float scale = max(u_planeAspect / u_imageAspect, 1.0);
          vec2 coverUv = (vUv - 0.5) * scale + 0.5;
          coverUv = clamp(coverUv, 0.0, 1.0);

          float depth = texture2D(u_depth, coverUv).r;
          float sharpDepth = pow(depth, 1.0 + u_ghostReduction * 2.5);

          vec2 inputOffset = u_useGyro > 0.5 ? u_gyro : u_mouse;
          float depthFactor = sharpDepth * sharpDepth;
          vec2 center = vec2(0.5) + inputOffset * 0.15 * u_progress;
          vec2 delta = coverUv - center;
          delta.x *= u_aspect;

          float displace = u_strength * depthFactor * u_progress;
          vec2 offset = delta * displace;

          vec2 displacedUv = coverUv + offset;
          vec2 clampedUv = clamp(displacedUv, 0.005, 0.995);

          vec4 color = texture2D(u_image, clampedUv);
          gl_FragColor = color;
        }
      `,
      transparent: false,
    });
  }, [imageTex, depthTex, gyroEnabled, parallaxStrength, ghostReduction]);

  const bgMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({ map: bgTex, toneMapped: false });
  }, [bgTex]);

  useFrame(() => {
    if (!material) return;

    if (isActive) {
      const elapsed = (performance.now() - startTime.current) / 1000;
      const p = Math.min(elapsed / 1.2, 1.0);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      setProgress(eased);
      material.uniforms.u_progress.value = eased;
    }

    const inputX = gyroEnabled ? gyroPos.x : mousePos.x;
    const inputY = gyroEnabled ? gyroPos.y : mousePos.y;

    material.uniforms.u_mouse.value.set(mousePos.x, mousePos.y);
    material.uniforms.u_gyro.value.set(gyroPos.x, gyroPos.y);
    material.uniforms.u_useGyro.value = gyroEnabled ? 1.0 : 0.0;
    material.uniforms.u_ghostReduction.value = ghostReduction;

    if (imageTex?.image) {
      const imgAspect = imageTex.image.width / imageTex.image.height;
      material.uniforms.u_imageAspect.value = imgAspect;
      material.uniforms.u_aspect.value = size.width / size.height;
    }

    camera.position.x = inputX * cameraMoveAmount * progress;
    camera.position.y = inputY * cameraMoveAmount * progress;
    camera.position.z = 5;
    camera.lookAt(0, 0, 0);

    if (bgMeshRef.current) {
      bgMeshRef.current.position.x = -inputX * backgroundShift * progress;
      bgMeshRef.current.position.y = -inputY * backgroundShift * progress;
    }
  });

  const distance = 5;
  const vFOV = (camera.fov * Math.PI) / 180;
  const planeHeight = 2 * Math.tan(vFOV / 2) * distance * zoom;
  const planeWidth = planeHeight * (size.width / size.height);

  if (material) {
    material.uniforms.u_planeAspect.value = planeWidth / planeHeight;
  }

  return (
    <>
      <mesh ref={bgMeshRef} position={[0, 0, -1.0]} material={bgMaterial}>
        <planeGeometry args={[planeWidth, planeHeight]} />
      </mesh>
      <mesh ref={meshRef} material={material} position={[0, 0, 0]}>
        <planeGeometry args={[planeWidth, planeHeight]} />
      </mesh>
    </>
  );
};

// ============================================
// CONTROL PANELS
// ============================================
const DepthTuningPanel = ({ params, setParams, onRegenerate, isRegenerating }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCylinderSub, setShowCylinderSub] = useState(false);
  const [showCornerPlusSub, setShowCornerPlusSub] = useState(false);
  
  return (
    <div style={panelStyles.panel}>
      <h4 style={panelStyles.title}>Depth Map Tuning</h4>
      
      {/* Basic Controls */}
      <div style={panelStyles.section}>
        <Slider label="Smoothing" value={params.depthSmoothing} min={0.1} max={3.0} step={0.1}
          onChange={(v) => setParams(p => ({ ...p, depthSmoothing: v }))} />
        <Slider label="Edge Threshold" value={params.edgeThreshold} min={0.01} max={0.30} step={0.01}
          onChange={(v) => setParams(p => ({ ...p, edgeThreshold: v }))} />
        <Slider label="Contrast" value={params.depthContrast} min={0.5} max={2.5} step={0.1}
          onChange={(v) => setParams(p => ({ ...p, depthContrast: v }))} />
        <Slider label="Black Point" value={params.blackPoint} min={0.0} max={0.5} step={0.01}
          onChange={(v) => setParams(p => ({ ...p, blackPoint: v }))} />
        <Slider label="White Point" value={params.whitePoint} min={0.5} max={1.0} step={0.01}
          onChange={(v) => setParams(p => ({ ...p, whitePoint: v }))} />
        <Slider label="Midtone Shift" value={params.midtoneShift} min={-0.8} max={0.8} step={0.05}
          onChange={(v) => setParams(p => ({ ...p, midtoneShift: v }))} />
        <Slider label="Depth Gamma" value={params.depthGamma} min={0.5} max={2.0} step={0.05}
          onChange={(v) => setParams(p => ({ ...p, depthGamma: v }))} />
      </div>
      
      <button style={{ ...styles.button, width: '100%', marginTop: 8, marginBottom: 8 }}
        onClick={() => setShowAdvanced(!showAdvanced)}>
        {showAdvanced ? '▼ Hide Advanced' : '▶ Show Advanced Controls'}
      </button>
      
      <AnimatePresence>
        {showAdvanced && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={panelStyles.section}>
              <Slider label="Edge Enhancement" value={params.edgeEnhance} min={0.0} max={1.0} step={0.05}
                onChange={(v) => setParams(p => ({ ...p, edgeEnhance: v }))} />
              <Slider label="Corner Boost" value={params.cornerBoost} min={0.0} max={0.5} step={0.02}
                onChange={(v) => setParams(p => ({ ...p, cornerBoost: v }))} />
              
              {/* Cylinder Curvature with sub‑controls */}
              <div style={{ marginTop: 12, marginBottom: 4 }}>
                <Slider label="Cylinder Curvature" value={params.cylinderStrength} min={-0.5} max={0.5} step={0.01}
                  onChange={(v) => setParams(p => ({ ...p, cylinderStrength: v }))} />
                <button
                  style={{ ...styles.tinyButton, marginLeft: 0, marginTop: 4 }}
                  onClick={() => setShowCylinderSub(!showCylinderSub)}
                >
                  {showCylinderSub ? '▼' : '▶'} Edge Clamp, Gamma, Width & Height
                </button>
                {showCylinderSub && (
                  <div style={{ marginLeft: 12, marginTop: 6 }}>
                    <Slider label="Edge Clamp" value={params.cylinderEdgeClamp} min={0.1} max={2.0} step={0.05}
                      onChange={(v) => setParams(p => ({ ...p, cylinderEdgeClamp: v }))} />
                    <Slider label="Cylinder Gamma" value={params.cylinderGamma} min={0.5} max={3.0} step={0.05}
                      onChange={(v) => setParams(p => ({ ...p, cylinderGamma: v }))} />
                    <Slider label="Width Spread" value={params.cylinderWidth} min={0.3} max={2.5} step={0.05}
                      onChange={(v) => setParams(p => ({ ...p, cylinderWidth: v }))} />
                    <Slider label="Height Spread" value={params.cylinderHeight} min={0.3} max={2.5} step={0.05}
                      onChange={(v) => setParams(p => ({ ...p, cylinderHeight: v }))} />
                    <div style={{ fontSize: 10, color: '#888', marginTop: -4 }}>
                      Width/Height control gradient spread
                    </div>
                  </div>
                )}
              </div>

              {/* Corner‑Plus Emphasis */}
              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <Slider label="Corner‑Plus Emphasis" value={params.cornerPlusStrength} min={-0.5} max={0.5} step={0.01}
                  onChange={(v) => setParams(p => ({ ...p, cornerPlusStrength: v }))} />
                <button
                  style={{ ...styles.tinyButton, marginLeft: 0, marginTop: 4 }}
                  onClick={() => setShowCornerPlusSub(!showCornerPlusSub)}
                >
                  {showCornerPlusSub ? '▼' : '▶'} Corner Gamma
                </button>
                {showCornerPlusSub && (
                  <div style={{ marginLeft: 12, marginTop: 6 }}>
                    <Slider label="Corner Gamma" value={params.cornerPlusGamma} min={0.5} max={3.0} step={0.05}
                      onChange={(v) => setParams(p => ({ ...p, cornerPlusGamma: v }))} />
                    <div style={{ fontSize: 10, color: '#888', marginTop: -4 }}>
                      Gamma &gt;1 makes corners darker
                    </div>
                  </div>
                )}
              </div>
              
              <Slider label="Local Contrast" value={params.localContrast} min={0.0} max={0.5} step={0.02}
                onChange={(v) => setParams(p => ({ ...p, localContrast: v }))} />
              <Slider label="Midtone Spread" value={params.midtoneSpread} min={0.0} max={0.8} step={0.05}
                onChange={(v) => setParams(p => ({ ...p, midtoneSpread: v }))} />
              <Slider label="Morph Radius" value={params.morphRadius} min={0} max={8} step={1}
                onChange={(v) => setParams(p => ({ ...p, morphRadius: v }))} />
              <Slider label="Radial Falloff" value={params.radialFalloff} min={0.0} max={0.5} step={0.02}
                onChange={(v) => setParams(p => ({ ...p, radialFalloff: v }))} />
              <Slider label="Noise Reduction" value={params.noiseReduction} min={0.0} max={1.0} step={0.05}
                onChange={(v) => setParams(p => ({ ...p, noiseReduction: v }))} />
              <Slider label="Detail Preserve" value={params.detailPreserve} min={0.0} max={1.0} step={0.05}
                onChange={(v) => setParams(p => ({ ...p, detailPreserve: v }))} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <button style={{ ...styles.button, ...styles.buttonPrimary, width: '100%', marginTop: 12 }}
        onClick={onRegenerate} disabled={isRegenerating}>
        {isRegenerating ? 'Regenerating...' : '🔄 Regenerate Depth'}
      </button>
    </div>
  );
};

const LiveControlPanel = ({ params, setParams, isVisible, onToggle }) => (
  <div style={controlStyles.panel}>
    <button style={controlStyles.toggle} onClick={onToggle}>
      {isVisible ? '▼ Hide Live Controls' : '▶ Show Live Controls'}
    </button>
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          style={controlStyles.content}
        >
          <div style={controlStyles.section}>
            <h4 style={controlStyles.sectionTitle}>View & Camera</h4>
            <Slider label="Zoom" value={params.zoom} min={1.0} max={2.0} step={0.01}
              onChange={(v) => setParams(p => ({ ...p, zoom: v }))} />
            <Slider label="Camera Move" value={params.cameraMove} min={0.1} max={0.8} step={0.05}
              onChange={(v) => setParams(p => ({ ...p, cameraMove: v }))} />
            <Slider label="Bg Shift" value={params.backgroundShift} min={0.0} max={0.25} step={0.005}
              onChange={(v) => setParams(p => ({ ...p, backgroundShift: v }))} />
          </div>
          <div style={controlStyles.section}>
            <h4 style={controlStyles.sectionTitle}>Parallax</h4>
            <Slider label="Strength" value={params.parallaxStrength} min={0.02} max={0.25} step={0.005}
              onChange={(v) => setParams(p => ({ ...p, parallaxStrength: v }))} />
            <Slider label="Ghost Reduction" value={params.ghostingReduction} min={0.0} max={3.0} step={0.05}
              onChange={(v) => setParams(p => ({ ...p, ghostingReduction: v }))} />
          </div>
          <button style={{ ...styles.button, marginTop: 8, width: '100%' }}
            onClick={() => setParams(p => ({
              ...p,
              zoom: DEFAULT_ZOOM,
              parallaxStrength: DEFAULT_PARALLAX_STRENGTH,
              cameraMove: DEFAULT_CAMERA_MOVE,
              backgroundShift: DEFAULT_BACKGROUND_SHIFT,
              ghostingReduction: DEFAULT_GHOST_REDUCTION,
            }))}>
            Reset Live Controls
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Slider = ({ label, value, min, max, step, onChange }) => (
  <div style={controlStyles.slider}>
    <label>{label}: {value.toFixed(3)}</label>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))} />
  </div>
);

const panelStyles = {
  panel: { width: '100%', backgroundColor: '#1a1a2e', borderRadius: 16, padding: 16, marginTop: 16 },
  title: { fontSize: 14, color: '#a855f7', margin: '0 0 12px 0', textAlign: 'center' },
  section: { display: 'flex', flexDirection: 'column', gap: 8 },
};

const controlStyles = {
  panel: { width: '100%', maxWidth: 420, marginTop: 16, backgroundColor: '#1a1a2e', borderRadius: 16, overflow: 'hidden' },
  toggle: { width: '100%', padding: '10px', backgroundColor: '#2a2a4a', color: '#fff', border: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  content: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' },
  section: { marginBottom: 8, borderBottom: '1px solid #3a3a5a', paddingBottom: 8 },
  sectionTitle: { fontSize: 13, color: '#a855f7', margin: '0 0 8px 0', fontWeight: 600 },
  slider: { display: 'flex', flexDirection: 'column', gap: 2, color: '#ddd', fontSize: 12, marginBottom: 6 },
};

// ============================================
// MAIN COMPONENT
// ============================================
const ImageMapTest = () => {
  const [image, setImage] = useState(null);
  const [imageElement, setImageElement] = useState(null);
  const [depthMap, setDepthMap] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [stage, setStage] = useState('upload');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [gyroEnabled, setGyroEnabled] = useState(false);
  const [gyroPermission, setGyroPermission] = useState('prompt');
  const [processingStatus, setProcessingStatus] = useState('');
  const [error, setError] = useState(null);
  const [useAIDepth, setUseAIDepth] = useState(true);
  const [showLiveControls, setShowLiveControls] = useState(false);

  const [params, setParams] = useState({
    zoom: DEFAULT_ZOOM,
    cameraMove: DEFAULT_CAMERA_MOVE,
    backgroundShift: DEFAULT_BACKGROUND_SHIFT,
    parallaxStrength: DEFAULT_PARALLAX_STRENGTH,
    ghostingReduction: DEFAULT_GHOST_REDUCTION,
    depthSmoothing: DEFAULT_DEPTH_SMOOTHING,
    edgeThreshold: DEFAULT_EDGE_THRESHOLD,
    depthContrast: DEFAULT_DEPTH_CONTRAST,
    blackPoint: DEFAULT_BLACK_POINT,
    whitePoint: DEFAULT_WHITE_POINT,
    midtoneShift: DEFAULT_MIDTONE_SHIFT,
    depthGamma: DEFAULT_DEPTH_GAMMA,
    edgeEnhance: DEFAULT_EDGE_ENHANCE,
    cornerBoost: DEFAULT_CORNER_BOOST,
    cylinderStrength: DEFAULT_CYLINDER_STRENGTH,
    cylinderEdgeClamp: DEFAULT_CYLINDER_EDGE_CLAMP,
    cylinderGamma: DEFAULT_CYLINDER_GAMMA,
    cylinderWidth: DEFAULT_CYLINDER_WIDTH,
    cylinderHeight: DEFAULT_CYLINDER_HEIGHT,
    cornerPlusStrength: DEFAULT_CORNER_PLUS_STRENGTH,
    cornerPlusGamma: DEFAULT_CORNER_PLUS_GAMMA,
    localContrast: DEFAULT_LOCAL_CONTRAST,
    midtoneSpread: DEFAULT_MIDTONE_SPREAD,
    morphRadius: DEFAULT_MORPH_RADIUS,
    radialFalloff: DEFAULT_RADIAL_FALLOFF,
    noiseReduction: DEFAULT_NOISE_REDUCTION,
    detailPreserve: DEFAULT_DETAIL_PRESERVE,
  });

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        setImage(e.target.result);
        setImageElement(img);
        setDepthMap(null);
        setBackgroundUrl(null);
        setStage('upload');
        setError(null);
      };
    };
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1,
  });

  const enableGyroscope = async () => {
    if (typeof DeviceOrientationEvent === 'undefined') {
      setGyroPermission('denied');
      return;
    }
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          setGyroEnabled(true);
          setGyroPermission('granted');
        } else {
          setGyroPermission('denied');
        }
      } catch (err) {
        setGyroPermission('denied');
      }
    } else {
      setGyroEnabled(true);
      setGyroPermission('granted');
    }
  };

  const generateDepth = async () => {
    if (!imageElement) return;
    setIsRegenerating(true);
    setError(null);
    try {
      let depthResult;
      if (useAIDepth) {
        try {
          depthResult = await generateAIDepthMap(imageElement, params);
        } catch (aiErr) {
          console.warn('AI depth failed, using heuristic', aiErr);
          depthResult = await generateHeuristicDepthMap(imageElement, params);
        }
      } else {
        depthResult = await generateHeuristicDepthMap(imageElement, params);
      }
      setDepthMap(depthResult.depthMapUrl);
    } catch (err) {
      setError(`Depth generation failed: ${err.message}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  const processImage = async () => {
    if (!imageElement) {
      setError('Please upload an image first.');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      await generateDepth();
      setProcessingStatus('🎨 Preparing background...');
      const bgUrl = await createBlurredBackground(image, 20);
      setBackgroundUrl(bgUrl);
      setStage('tuning');
    } catch (err) {
      setError(`Processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinueTo3D = () => setStage('view3D');
  const handleBackToTuning = () => setStage('tuning');
  const handleNewImage = () => {
    setImage(null); setImageElement(null); setDepthMap(null); setBackgroundUrl(null); setStage('upload');
  };

  return (
    <div style={styles.container}>
      <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={styles.header}>
        <h1 style={styles.title}>Depth Diorama V8.7</h1>
        <p style={styles.subtitle}>
          {stage === 'upload' && 'Upload an image to begin'}
          {stage === 'tuning' && 'Fine‑tune depth map • Regenerate as needed'}
          {stage === 'view3D' && 'Interactive 3D • Live controls active'}
        </p>
      </motion.div>

      <div style={styles.content}>
        {stage === 'upload' && (
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={styles.uploadWrapper}>
            <div {...getRootProps()} style={{ ...styles.dropzone, ...(isDragActive ? styles.dropzoneActive : {}) }}>
              <input {...getInputProps()} />
              <div style={styles.dropzoneContent}>
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="1.5" />
                  <polyline points="17 8 12 3 7 8" strokeWidth="1.5" />
                  <line x1="12" y1="3" x2="12" y2="15" strokeWidth="1.5" />
                </svg>
                <p style={styles.dropzoneText}>{isDragActive ? 'Drop image here' : 'Drag & drop an image'}</p>
                <p style={styles.dropzoneHint}>or tap to select</p>
              </div>
            </div>
            <div style={styles.toggleContainer}>
              <label style={styles.toggleLabel}>
                <input type="checkbox" checked={useAIDepth} onChange={(e) => setUseAIDepth(e.target.checked)} style={styles.toggle} />
                Use AI Depth (slower, more accurate)
              </label>
            </div>
            {image && (
              <button style={{ ...styles.button, ...styles.buttonPrimary, width: '100%', marginTop: 16 }}
                onClick={processImage} disabled={isProcessing}>
                {isProcessing ? 'Processing...' : '✨ Generate Depth Map'}
              </button>
            )}
          </motion.div>
        )}

        {stage === 'tuning' && depthMap && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.tuningContainer}>
            <div style={styles.previewContainer}>
              <img src={image} alt="Original" style={styles.previewImage} />
              <img src={depthMap} alt="Depth" style={styles.previewImage} />
            </div>
            <DepthTuningPanel params={params} setParams={setParams} onRegenerate={generateDepth} isRegenerating={isRegenerating} />
            <div style={styles.buttonRow}>
              <button style={styles.button} onClick={handleNewImage}>← New Image</button>
              <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={handleContinueTo3D}>Continue to 3D →</button>
            </div>
          </motion.div>
        )}

        {stage === 'view3D' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.viewer}>
            <div id="canvas-wrapper" style={styles.canvasContainer}>
              <ErrorBoundary>
                <Canvas
                  camera={{ position: [0, 0, 5], fov: 45 }}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  gl={{ preserveDrawingBuffer: true, antialias: true, alpha: false, toneMapping: THREE.NoToneMapping }}
                  onCreated={({ gl }) => gl.setClearColor('#0a0a0f', 1)}
                >
                  <ambientLight intensity={1.5} />
                  {image && depthMap && backgroundUrl ? (
                    <ShaderParallax
                      imageUrl={image}
                      depthUrl={depthMap}
                      backgroundUrl={backgroundUrl}
                      isActive={true}
                      gyroEnabled={gyroEnabled}
                      zoom={params.zoom}
                      parallaxStrength={params.parallaxStrength}
                      cameraMoveAmount={params.cameraMove}
                      backgroundShift={params.backgroundShift}
                      ghostReduction={params.ghostingReduction}
                    />
                  ) : (
                    <Html center><div style={styles.htmlPlaceholder}>Loading...</div></Html>
                  )}
                </Canvas>
              </ErrorBoundary>
            </div>

            <div style={styles.controls}>
              <button style={styles.button} onClick={handleBackToTuning}>← Back to Tuning</button>
              <button style={styles.button} onClick={handleNewImage}>New Image</button>
              {gyroPermission !== 'denied' && (
                <button style={{ ...styles.button, ...(gyroEnabled ? styles.buttonActive : {}) }}
                  onClick={gyroEnabled ? () => setGyroEnabled(false) : enableGyroscope}>
                  {gyroEnabled ? '📱 Gyro On' : '📱 Gyro'}
                </button>
              )}
            </div>

            <LiveControlPanel params={params} setParams={setParams} isVisible={showLiveControls} onToggle={() => setShowLiveControls(!showLiveControls)} />
            <p style={styles.instruction}>{gyroEnabled ? 'Tilt device to explore' : 'Move mouse to look around'}</p>
          </motion.div>
        )}

        {error && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.error}>⚠️ {error}</motion.div>}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        body, html { margin: 0; padding: 0; width: 100%; background: #0a0a0f; }
        #root { width: 100%; }
      `}</style>
    </div>
  );
};

// ============================================
// STYLES
// ============================================
const styles = {
  container: {
    minHeight: '100vh', width: '100%', backgroundColor: '#0a0a0f', color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '16px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box',
  },
  header: { textAlign: 'center', padding: '16px 0 8px', width: '100%' },
  title: {
    fontSize: 'clamp(22px, 5vw, 32px)', fontWeight: 600, margin: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #a855f7 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: '12px', color: '#888', margin: '6px 0 0', letterSpacing: 0.5 },
  content: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '500px', margin: '0 auto' },
  uploadWrapper: { width: '100%' },
  dropzone: {
    border: '2px dashed #333', borderRadius: '20px', padding: '56px 24px', textAlign: 'center', cursor: 'pointer',
    transition: 'all 0.3s', backgroundColor: '#1a1a2e', width: '100%', boxSizing: 'border-box',
  },
  dropzoneActive: { borderColor: '#a855f7', backgroundColor: '#1e1e3a', transform: 'scale(1.01)' },
  dropzoneContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  dropzoneText: { fontSize: '16px', margin: 0, fontWeight: 500 },
  dropzoneHint: { fontSize: '13px', color: '#666', margin: 0 },
  toggleContainer: { marginTop: 16, textAlign: 'center' },
  toggleLabel: { fontSize: 13, color: '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' },
  toggle: { width: 16, height: 16, cursor: 'pointer' },
  tuningContainer: { width: '100%' },
  previewContainer: { display: 'flex', gap: 8, marginBottom: 16 },
  previewImage: { width: '50%', borderRadius: 12, border: '1px solid #333', objectFit: 'cover' },
  buttonRow: { display: 'flex', gap: 10, marginTop: 16 },
  viewer: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  canvasContainer: {
    aspectRatio: '9/16', width: '100%', maxWidth: '400px', backgroundColor: '#0a0a0f', borderRadius: 24,
    overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.7)', position: 'relative', margin: '0 auto', border: '1px solid #2a2a4a',
  },
  htmlPlaceholder: { color: '#888', fontSize: 14, textAlign: 'center' },
  controls: { display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap', justifyContent: 'center', width: '100%' },
  button: {
    padding: '11px 20px', backgroundColor: '#2a2a4a', color: '#fff', border: 'none', borderRadius: 30, fontSize: 13,
    fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', flex: '0 1 auto',
  },
  buttonPrimary: { background: 'linear-gradient(135deg, #667eea 0%, #a855f7 100%)' },
  buttonActive: { backgroundColor: '#a855f7' },
  tinyButton: {
    padding: '2px 10px', backgroundColor: '#3a3a5a', color: '#ccc', border: 'none', borderRadius: 12, fontSize: 11,
    cursor: 'pointer', marginLeft: 8,
  },
  instruction: { textAlign: 'center', fontSize: 12, color: '#777', marginTop: 16 },
  error: {
    marginTop: 16, padding: '12px 20px', backgroundColor: 'rgba(200,30,30,0.15)', border: '1px solid rgba(200,30,30,0.4)',
    borderRadius: 40, fontSize: 13, color: '#ff8080', textAlign: 'center', width: '100%',
  },
  errorContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 20, textAlign: 'center', color: '#ff8080' },
};

export default ImageMapTest;